import { createSkeletonProvider } from '@trainline/react-skeletor'
import { TreeProps } from 'antd/lib/tree'
import gql from 'graphql-tag'
import { capitalize, difference, groupBy } from 'lodash'
import * as React from 'react'
import { Query } from 'react-apollo'
import styled from 'styled-components'
import Tree from 'web-makeup/dist/Tree'

import createSkeletonElement from './../../helpers/tomb'
import { GetAllRequestTicketTypes } from './../../typings/types'

const { TreeNode } = Tree

interface Props {
  disabled?: boolean
  // This is `onChange` instead of `onCheck` because it is injected by
  // `getFieldDecorator` so I wanna keep it consistent.
  onChange?: TreeProps['onCheck']
  defaultCheckedKeys?: string[] | null
  value?: string[] | null
}

export const TreeNodeProvider = createSkeletonProvider(
  { children: '________________' },
  ({ children }) => children === undefined
)(createSkeletonElement('span'))

const StyledTreeNode = styled<{
  key?: string | null
  title?: string | null
}>(TreeNodeProvider)`
  && {
    display: inline-block;
    height: 1.5rem;
    margin-bottom: 0.75rem;
  }
`

export const StyledDiv = styled.div`
  && {
    line-height: 1;
    padding-top: 0.5rem;
  }
`

export const GET_ALL_REQUEST_TICKET_TYPES = gql`
  query getAllRequestTicketTypes {
    allRequestTicketTypes {
      category
      subtext
      subcategory
      requestType
      title
      uuid
    }
  }
`

export class AllRequestTicketTypesQuery extends Query<
  GetAllRequestTicketTypes.Query,
  GetAllRequestTicketTypes.Variables
> {}

export const mapRequestTicketTypesToTree = requestTicketTypes =>
  groupBy(requestTicketTypes, 'requestType')

// Because antd.Tree#onCheck includes the `key` of the main node,
// we need to remove the main node and ONLY return the nested nodes' uuids.
const excludeRequestTypeKeys = (onChange, types) => (checkedKeys, event) =>
  onChange && onChange(difference(checkedKeys, Object.keys(types)), event)

type RTProps = {
  data?: GetAllRequestTicketTypes.Query['allRequestTicketTypes']
  loading: boolean
} & Props

export const RequestTicketsTree: React.SFC<RTProps> = ({
  defaultCheckedKeys,
  data,
  loading,
  disabled,
  onChange,
  value: checkedKeys,
}: RTProps) => {
  const allRequestTicketTypesTree = mapRequestTicketTypesToTree(data)

  return loading ? (
    <React.Fragment>
      {[1, 2, 3, 4, 5, 6, 7].map(i => (
        <StyledDiv key={i}>
          <StyledTreeNode />
        </StyledDiv>
      ))}
    </React.Fragment>
  ) : (
    <Tree
      disabled={disabled}
      checkable
      defaultCheckedKeys={checkedKeys || defaultCheckedKeys || undefined}
      onCheck={excludeRequestTypeKeys(onChange, allRequestTicketTypesTree)}
    >
      {Object.keys(allRequestTicketTypesTree).map(type => {
        const categories = allRequestTicketTypesTree[type]
        const capitalizedType = capitalize(type)

        return categories.length > 1 ? (
          <TreeNode title={capitalizedType} key={type}>
            {categories.map(
              category => category.title && <TreeNode title={category.title} key={category.uuid} />
            )}
          </TreeNode>
        ) : (
          <TreeNode title={capitalizedType} key={categories[0].uuid} />
        )
      })}
    </Tree>
  )
}

// Cannot be SFC because it needs to be used with `getFieldDecorator`.
class RequestTypes extends React.Component<Props> {
  render() {
    const { value, defaultCheckedKeys, onChange, disabled } = this.props

    return (
      <AllRequestTicketTypesQuery query={GET_ALL_REQUEST_TICKET_TYPES}>
        {({ data, loading }) => (
          <RequestTicketsTree
            value={value}
            defaultCheckedKeys={defaultCheckedKeys}
            onChange={onChange}
            data={data && data.allRequestTicketTypes}
            loading={loading}
            disabled={disabled}
          />
        )}
      </AllRequestTicketTypesQuery>
    )
  }
}

export default RequestTypes



// WEBPACK FOOTER //
// ./src/components/RequestTypes/RequestTypes.tsx